//ModalColaborador - Formulario de edición/creación de colaboradores
import React, { useState, useEffect } from "react";
import { listRoles } from "../services/rolesService";
import Modal from "antd/es/modal/Modal";

// *** Lista de roles NO permitidos para colaboradores
const FORBIDDEN_ROLE_KEYWORDS = ["representante", "deportista"];

function isForbiddenRole(role) {
  if (!role) return false;

  const raw =
    String(role.slug || role.codigo || role.nombre || "")
      .toLowerCase()
      .trim();

  return FORBIDDEN_ROLE_KEYWORDS.includes(raw);
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder,
  disabled = false,
  onlyDigits = false,
  maxLength,
  inputProps = {},
  error,
}) {
  const handleChange = (e) => {
    let v = e.target.value;
    if (onlyDigits) {
      v = v.replace(/\D/g, "").slice(0, maxLength || Infinity);
    }
    onChange(v);
  };

  const mergedInputProps = { ...inputProps };
  if (maxLength) mergedInputProps.maxLength = maxLength;

  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-700 mb-1">
        {label} {required && <span className="text-rose-600">*</span>}
      </span>
      <input
        type={type}
        value={value ?? ""}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
        required={required}
        {...mergedInputProps}
      />
      {error && <p className="mt-1 text-xs text-rose-600">{error}</p>}
    </label>
  );
}

export function ModalColaborador({
  open,
  colaborador,
  saving,
  onCancel,
  onSave,
}) {
  const isEditing = !!colaborador?.id;
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    id: "",
    nombres: "",
    apellidos: "",
    email: "",
    password: "",
    rol_id: "",
    telefono: "",
    cedula: "",
    fecha_nacimiento: "",
    direccion: "",
    inicio_contrato: "",
    fin_contrato: "",
    observaciones: "",
  });

  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showPwdTooltip, setShowPwdTooltip] = useState(false);

  // Cargar datos del colaborador
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 100));

        if (colaborador) {
          setFormData({
            id: colaborador.id || "",
            nombres: colaborador.nombres || "",
            apellidos: colaborador.apellidos || "",
            email: colaborador.email || "",
            password: "",
            rol_id: colaborador.rol_id || colaborador.roles?.id || "",
            telefono: colaborador.telefono || "",
            cedula: colaborador.cedula || "",
            fecha_nacimiento:
              colaborador.fecha_nacimiento?.slice(0, 10) || "",
            direccion: colaborador.direccion || "",
            inicio_contrato:
              colaborador.inicio_contrato?.slice(0, 10) || "",
            fin_contrato: colaborador.fin_contrato?.slice(0, 10) || "",
            observaciones: colaborador.observaciones || "",
          });
        } else {
          setFormData({
            id: "",
            nombres: "",
            apellidos: "",
            email: "",
            password: "",
            rol_id: "",
            telefono: "",
            cedula: "",
            fecha_nacimiento: "",
            direccion: "",
            inicio_contrato: "",
            fin_contrato: "",
            observaciones: "",
          });
        }
      } catch (error) {
        console.error("Error cargando datos del colaborador:", error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      loadData();
    }
  }, [colaborador, open]);

  // Cargar roles activos y filtrar los no permitidos
  useEffect(() => {
    let mounted = true;

    const loadRoles = async () => {
      setRolesLoading(true);
      try {
        const all = await listRoles();
        if (!mounted) return;

        let filtered = Array.isArray(all) ? all.filter((r) => r.activo) : [];

        // *** Aquí filtramos representante / deportista
        filtered = filtered.filter((r) => !isForbiddenRole(r));

        setRoles(filtered);
      } catch (err) {
        console.error("Error cargando roles:", err);
        setRoles([]);
      } finally {
        if (mounted) setRolesLoading(false);
      }
    };

    if (open) loadRoles();

    return () => {
      mounted = false;
    };
  }, [open]);

  // Limpiar errores y estado de password al cerrar
  useEffect(() => {
    if (!open) {
      setErrors({});
      setShowPassword(false);
      setShowPwdTooltip(false);
      setFormData((f) => ({ ...f, password: "" }));
    }
  }, [open]);

  // Validación de contraseña (live)
  const pwd = formData.password || "";
  const specialRegex = new RegExp("[!@#$%^&*()_+=;:,.<>/?-]");
  const allowedOnlyRegex = new RegExp(
    "^[A-Za-z0-9!@#$%^&*()_+=;:,.<>/?-]*$"
  );
  const pwdChecks = {
    length: pwd.length >= 8 && pwd.length <= 15,
    upper: /[A-Z]/.test(pwd),
    lower: /[a-z]/.test(pwd),
    digit: /\d/.test(pwd),
    special: specialRegex.test(pwd),
    allowedOnly: allowedOnlyRegex.test(pwd),
  };
  const pwdAllValid =
    pwdChecks.length &&
    pwdChecks.upper &&
    pwdChecks.lower &&
    pwdChecks.digit &&
    pwdChecks.special &&
    pwdChecks.allowedOnly;
  const pwdScore = Object.values(pwdChecks).reduce(
    (s, v) => s + (v ? 1 : 0),
    0
  );
  const totalChecks = Object.keys(pwdChecks).length;
  const pwdPercent = Math.round((pwdScore / totalChecks) * 100);
  let pwdBarClass = "bg-rose-500";
  if (pwdScore === totalChecks) {
    pwdBarClass = "bg-emerald-500";
  } else if (pwdScore >= totalChecks - 2) {
    pwdBarClass = "bg-amber-400";
  } else if (pwdScore >= 2) {
    pwdBarClass = "bg-yellow-400";
  } else {
    pwdBarClass = "bg-rose-500";
  }
  const barWidth = `${pwdPercent}%`;

  const handleSubmit = (e) => {
    e.preventDefault();
    const email = (formData.email || "").trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const allowedDomains = [
      "gmail.com",
      "hotmail.com",
      "outlook.com",
      "yahoo.com",
      "live.com",
      "icloud.com",
    ];

    const newErrors = {};

    // Email
   if (!email) {
  newErrors.email = "El correo electrónico es obligatorio.";
} else if (!email.includes("@")) {
  newErrors.email = "El correo debe contener el símbolo '@'.";
} else if (!emailRegex.test(email)) {
  newErrors.email = "Ingrese un correo electrónico válido.";
} else {
  const parts = email.split("@");
  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    newErrors.email = "Ingrese un correo electrónico válido.";
  } else {
    const domain = parts[1].toLowerCase();
    if (!domain.includes(".")) {
      newErrors.email = "El correo electrónico no es válido.";
    } else if (!allowedDomains.includes(domain)) {
      newErrors.email = "El dominio del correo es incorrecto.";
    }
  }
}

    // Validación de fecha de nacimiento (mayor de 18 años)
if (formData.fecha_nacimiento) {
  const birthDate = new Date(formData.fecha_nacimiento);
  const today = new Date();
  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();

  if (
    age < 18 ||
    (age === 18 && monthDiff < 0) ||
    (age === 18 && monthDiff === 0 && dayDiff < 0)
  ) {
    newErrors.fecha_nacimiento =
      "El colaborador debe ser mayor de 18 años.";
  }
}

    // Teléfono y cédula
    if (formData.telefono && formData.telefono.length > 10) {
      newErrors.telefono =
        "El teléfono debe tener como máximo 10 dígitos.";
    }

    if (formData.cedula && formData.cedula.length > 10) {
      newErrors.cedula =
        "La cédula debe tener como máximo 10 dígitos.";
    }

    // *** Validar rol seleccionado
    if (!formData.rol_id) {
      newErrors.rol_id = "El rol es obligatorio.";
    } else {
      const selectedRole = roles.find((r) => r.id === formData.rol_id);
      if (selectedRole && isForbiddenRole(selectedRole)) {
        newErrors.rol_id =
          "No puede asignar el rol de Representante o Deportista a un colaborador.";
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    // Password sólo al crear
    if (!isEditing) {
      if (!formData.password || String(formData.password).trim() === "") {
        newErrors.password = "Password requerido";
        setErrors(newErrors);
        return;
      }

      if (!pwdAllValid) {
        newErrors.password =
          "La contraseña no cumple los requisitos de seguridad.";
        setErrors(newErrors);
        return;
      }
    }

    // Validaciones de fechas
    const _tomorrow = new Date();
    _tomorrow.setDate(_tomorrow.getDate() + 1);
    const minAllowed = _tomorrow.toISOString().slice(0, 10);

    if (formData.fin_contrato) {
      if (formData.fin_contrato < minAllowed) {
        newErrors.fin_contrato =
          "La fecha de fin no puede ser anterior a mañana.";
      }
      if (
        formData.inicio_contrato &&
        formData.fin_contrato < formData.inicio_contrato
      ) {
        newErrors.fin_contrato =
          "La fecha de fin no puede ser anterior a la fecha de inicio.";
      }
    }

    if (formData.inicio_contrato && formData.fin_contrato) {
      if (formData.inicio_contrato > formData.fin_contrato) {
        newErrors.inicio_contrato =
          "La fecha de inicio no puede ser posterior a la fecha de fin.";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(formData);
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={isEditing ? "Editar colaborador" : "Nuevo colaborador"}
      footer={null}
      width={700}
    >
      {loading ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="space-y-1">
                <div className="h-3 w-20 bg-slate-200 rounded animate-pulse" />
                <div className="h-9 w-full bg-slate-100 rounded-xl animate-pulse" />
              </div>
            ))}
          </div>
          <div className="space-y-1">
            <div className="h-3 w-24 bg-slate-200 rounded animate-pulse" />
            <div className="h-20 w-full bg-slate-100 rounded-xl animate-pulse" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <div className="h-9 w-20 bg-slate-100 rounded-xl animate-pulse" />
            <div className="h-9 w-32 bg-slate-200 rounded-xl animate-pulse" />
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field
              label="Nombres"
              value={formData.nombres}
              onChange={(v) =>
                setFormData((f) => ({ ...f, nombres: v }))
              }
              required
              disabled={saving}
              placeholder="Ej: Juan"
            />
            <Field
              label="Apellidos"
              value={formData.apellidos}
              onChange={(v) =>
                setFormData((f) => ({ ...f, apellidos: v }))
              }
              required
              disabled={saving}
              placeholder="Ej: García López"
            />
            <Field
              label="Email"
              type="email"
              value={formData.email}
              onChange={(v) => {
                setFormData((f) => ({ ...f, email: v }));
                if (v && String(v).trim() !== "")
                  setErrors((s) => ({ ...s, email: undefined }));
              }}
              required
              disabled={saving}
              placeholder="correo@ejemplo.com"
              error={errors.email}
            />

            {!isEditing && (
              <label className="block">
                <span className="block text-xs font-medium text-slate-700 mb-1">
                  Password <span className="text-rose-600">*</span>
                </span>
                <div
                  className="relative"
                  onMouseEnter={() => setShowPwdTooltip(true)}
                  onMouseLeave={() => setShowPwdTooltip(false)}
                >
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password ?? ""}
                      onChange={(e) => {
                        const v = e.target.value.slice(0, 15);
                        setFormData((f) => ({ ...f, password: v }));
                        if (!v)
                          setErrors((s) => ({
                            ...s,
                            password: undefined,
                          }));
                      }}
                      onFocus={() => setShowPwdTooltip(true)}
                      onBlur={() => setShowPwdTooltip(false)}
                      placeholder="Contraseña inicial"
                      disabled={saving}
                      required
                      maxLength={15}
                      className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm pr-10 focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowPassword((s) => !s)
                      }
                      className="absolute right-3 inset-y-0 flex items-center text-slate-500 hover:text-slate-700 focus:outline-none"
                      tabIndex={0}
                      aria-label={
                        showPassword
                          ? "Ocultar contraseña"
                          : "Mostrar contraseña"
                      }
                    >
                      {showPassword ? (
                        <svg
                          className="w-5 h-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9.27-3.11-11-7 1.02-2.1 2.58-3.93 4.46-5.26m2.3-1.5A9.953 9.953 0 0112 5c5 0 9.27 3.11 11 7-1.02 2.1-2.58 3.93-4.46 5.26M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 3l18 18"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-5 h-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 15a3 3 0 100-6 3 3 0 000 6z"
                          />
                        </svg>
                      )}
                    </button>
                   {errors.password && !pwdAllValid && (
  <p className="absolute left-0 -bottom-7 text-xs text-rose-600">
    {errors.password}
  </p>
)}
                  </div>

                  {pwd.length > 0 && (
                    <div className="mt-2">
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`${pwdBarClass} h-full transition-all`}
                          style={{ width: barWidth }}
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                  )}

                  <div
                    role="tooltip"
                    className={`absolute z-50 right-0 mt-2 w-80 p-3 rounded-lg border bg-white text-xs text-slate-700 shadow-lg ${
                      showPwdTooltip ? "block" : "hidden"
                    }`}
                  >
                    <p className="mb-1 font-medium">
                      Contraseña segura — requisitos
                    </p>
                    <ul className="space-y-1">
                      <li
                        className={
                          pwdChecks.length
                            ? "text-emerald-600"
                            : "text-slate-500"
                        }
                      >
                        {pwdChecks.length ? "✓" : "•"} Tener entre 8 y
                        15 caracteres
                      </li>
                      <li
                        className={
                          pwdChecks.upper
                            ? "text-emerald-600"
                            : "text-slate-500"
                        }
                      >
                        {pwdChecks.upper ? "✓" : "•"} Al menos una
                        letra mayúscula (A-Z)
                      </li>
                      <li
                        className={
                          pwdChecks.lower
                            ? "text-emerald-600"
                            : "text-slate-500"
                        }
                      >
                        {pwdChecks.lower ? "✓" : "•"} Al menos una
                        letra minúscula (a-z)
                      </li>
                      <li
                        className={
                          pwdChecks.digit
                            ? "text-emerald-600"
                            : "text-slate-500"
                        }
                      >
                        {pwdChecks.digit ? "✓" : "•"} Al menos un
                        número (0-9)
                      </li>
                      <li
                        className={
                          pwdChecks.special
                            ? "text-emerald-600"
                            : "text-slate-500"
                        }
                      >
                        {pwdChecks.special ? "✓" : "•"} Al menos un
                        carácter especial permitido (! @ # $ % ^ &amp;{" "}
                        * ( ) _ + = ; : , . &lt; &gt; / ? -)
                      </li>
                      <li
                        className={
                          pwdChecks.allowedOnly
                            ? "text-emerald-600"
                            : "text-slate-500"
                        }
                      >
                        {pwdChecks.allowedOnly ? "✓" : "•"} Solo se
                        permiten letras, números y los caracteres
                        especiales mencionados
                      </li>
                    </ul>
                  </div>
                </div>
              </label>
            )}

            <Field
              label="Teléfono"
              value={formData.telefono}
              onChange={(v) => {
                setFormData((f) => ({ ...f, telefono: v }));
                if (v && String(v).length > 0)
                  setErrors((s) => ({ ...s, telefono: undefined }));
              }}
              disabled={saving}
              placeholder="Ej: 0987654321"
              onlyDigits
              required
              maxLength={10}
              inputProps={{
                inputMode: "numeric",
                pattern: "\\d*",
                title: "Sólo dígitos (máx. 10).",
              }}
              error={errors.telefono}
            />
            <Field
              label="Cédula"
              value={formData.cedula}
              onChange={(v) => {
                setFormData((f) => ({ ...f, cedula: v }));
                if (v && String(v).length > 0)
                  setErrors((s) => ({ ...s, cedula: undefined }));
              }}
              disabled={saving || isEditing}
              placeholder="Ej: 1234567890"
              onlyDigits
              required
              maxLength={10}
              inputProps={{
                inputMode: "numeric",
                pattern: "\\d*",
                title: "Sólo dígitos (máx. 10).",
              }}
              error={errors.cedula}
            />
            <Field
  label="Fecha de nacimiento"
  type="date"
  value={formData.fecha_nacimiento}
  onChange={(v) => {
    setFormData((f) => ({ ...f, fecha_nacimiento: v }));
    if (v && String(v).length > 0)
      setErrors((s) => ({ ...s, fecha_nacimiento: undefined }));
  }}
  disabled={saving || isEditing}
  placeholder="Selecciona una fecha"
  required
  inputProps={{
    max: (() => {
      const today = new Date();
      today.setFullYear(today.getFullYear() - 18);
      return today.toISOString().slice(0, 10);
    })(),
  }}
  error={errors.fecha_nacimiento}
/>
            <Field
              label="Dirección"
              value={formData.direccion}
              onChange={(v) =>
                setFormData((f) => ({ ...f, direccion: v }))
              }
              disabled={saving}
              required
              placeholder="Calle, número, ciudad"
            />
            <Field
              label="Inicio de contrato"
              type="date"
              value={formData.inicio_contrato}
              onChange={(v) => {
                if (formData.fin_contrato && v && v > formData.fin_contrato) {
                  setErrors((s) => ({
                    ...s,
                    inicio_contrato:
                      "La fecha de inicio no puede ser posterior a la fecha de fin.",
                  }));
                  return;
                }
                setErrors((s) => ({
                  ...s,
                  inicio_contrato: undefined,
                }));
                setFormData((f) => ({ ...f, inicio_contrato: v }));
              }}
              disabled={saving}
              placeholder="Selecciona una fecha"
              required
              inputProps={{ max: formData.fin_contrato || undefined }}
              error={errors.inicio_contrato}
            />
            <Field
              label="Fin de contrato"
              type="date"
              value={formData.fin_contrato}
              onChange={(v) => {
                const _tomorrow = new Date();
                _tomorrow.setDate(_tomorrow.getDate() + 1);
                const minDate = _tomorrow.toISOString().slice(0, 10);

                if (v && v < minDate) {
                  setErrors((s) => ({
                    ...s,
                    fin_contrato:
                      "La fecha de fin no puede ser anterior a mañana.",
                  }));
                  return;
                }
                if (
                  v &&
                  formData.inicio_contrato &&
                  v < formData.inicio_contrato
                ) {
                  setErrors((s) => ({
                    ...s,
                    fin_contrato:
                      "La fecha de fin no puede ser anterior a la fecha de inicio.",
                  }));
                  return;
                }
                setErrors((s) => ({
                  ...s,
                  fin_contrato: undefined,
                }));
                setFormData((f) => ({ ...f, fin_contrato: v }));
              }}
              disabled={saving}
              placeholder="Selecciona una fecha"
              inputProps={{
                min: (() => {
                  const _tomorrow = new Date();
                  _tomorrow.setDate(_tomorrow.getDate() + 1);
                  const t = _tomorrow.toISOString().slice(0, 10);
                  return formData.inicio_contrato &&
                    formData.inicio_contrato > t
                    ? formData.inicio_contrato
                    : t;
                })(),
              }}
              error={errors.fin_contrato}
            />
          </div>

          {/* Select de roles (sin representante/deportista) */}
          <label className="block">
            <span className="block text-xs font-medium text-slate-700 mb-1">
              Rol <span className="text-rose-600">*</span>
            </span>
            <select
              value={formData.rol_id ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                setFormData((f) => ({ ...f, rol_id: value }));
                if (value)
                  setErrors((s) => ({ ...s, rol_id: undefined }));
              }}
              disabled={saving || rolesLoading}
              required
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
            >
              <option value="">
                {rolesLoading
                  ? "Cargando roles..."
                  : "Selecciona un rol"}
              </option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.nombre}
                </option>
              ))}
            </select>
            {errors.rol_id && (
              <p className="mt-1 text-xs text-rose-600">
                {errors.rol_id}
              </p>
            )}
          </label>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Observaciones
            </label>
            <textarea
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
              rows={3}
              value={formData.observaciones}
              onChange={(e) =>
                setFormData((f) => ({
                  ...f,
                  observaciones: e.target.value,
                }))
              }
              disabled={saving}
              placeholder="Información adicional sobre el colaborador..."
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-blue-600 text-white px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-60 transition disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving && (
                <svg
                  className="animate-spin h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {saving
                ? "Guardando..."
                : isEditing
                ? "Guardar cambios"
                : "Crear colaborador"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
